import {
  CoreShareModal,
  FileUpdateContextThumbnailModal,
  TagConfirmTagMergeModal,
  TagDeleteModal,
  TagEditForm
} from '#components';

export function useTagActions() {

  function addEditTag({tag} = {}) {
    return new Promise(async resolve => {
      const modal = useFModal({
        id: 'add-edit-tag',
        clickToClose: false,
        component: TagEditForm,
        props: {tag},
        eventHandlers: {
          success({newTag}) {
            resolve(newTag);
            modal.close();
          }
        }
      });

      modal.open();
    })
  }

  async function share({tag}) {
    if (!tag.share_token) {
      tag = await useTagsStore().getTagsOne({id: tag.id});
    }

    useFModal({
      id: 'share-files',
      component: CoreShareModal,
      size: 'sm-md',
      props: {
        shareObject: tag,
        shareType: 'tag'
      }
    }).open()
  }

  function editThumbnail({tag}) {
    const modal = useFModal({
      id: 'edit-tag-cover',
      component: FileUpdateContextThumbnailModal,
      size: 'fullscreen',
      showClose: false,
      props: {
        contextType: FILE_CONTEXTS.tag,
        context: tag
      },
      eventHandlers: {
        async complete(file) {
          try {
            await useFileActions().setFileAsTagCover({tag, file});
            modal.close();
          } catch (e) {
            useErrorToast().add();
          }
        }
      }
    });

    modal.open();
  }

  function confirmTagMerge({tag, toTag}) {
    return new Promise(res => {
      const modal = useFModal({
        id: 'confirm-tag-merge',
        component: TagConfirmTagMergeModal,
        props: {
          tag,
          toTag
        },
        eventHandlers: {
          success() {
            res({merged: true});
          }
        }
      });

      modal.open();
    })
  }

  function deleteTags({tags}) {
    return new Promise(resolve => {
      const modal = useFModal({
        id: 'delete-tag',
        component: TagDeleteModal,
        props: {
          tags
        },
        eventHandlers: {
          success() {
            modal.close();
            resolve();
          }
        }
      });
      modal.open();
    });
  }

  function getContextActions({tag, userId, shareToken}) {
    return {
      upload: {
        label: 'Upload Files',
        icon: COMMON_ICONS.upload,
        click: () => useUploadActions().openUploaderModal({tag, shareToken, contextType: FILE_CONTEXTS.tag ,contextId: tag.id})
      },
      addFiles: {
        label: 'Add Files',
        icon: COMMON_ICONS.add,
        click: () => useFileActions().addFilesToContext({
          context: tag,
          contextType: FILE_CONTEXTS.tag,
          shareToken
        })
      },
      edit: {
        label: 'Edit Name',
        icon: COMMON_ICONS.edit,
        click: () => addEditTag({tag})
      },
      editCover: {
        label: 'Edit Cover Photo',
        icon: COMMON_ICONS.makeCover,
        click: () => {
          if (!tag.files_count) {
            useInfoToast().add({
              description: `You must add files to this tag before you can set a cover photo.`
            });
          } else {
            editThumbnail({tag});
          }
        }
      },
      share: {
        label: 'Share',
        icon: COMMON_ICONS.share,
        click: () => share({tag})
      },
      delete: {
        label: 'Delete',
        icon: COMMON_ICONS.delete,
        click: async () => {
          await deleteTags({tags: [tag]});
          navigateTo(useBuildRoute().toTags());
        }
      },
      declutter: {
        label: 'Declutter',
        icon: COMMON_ICONS.declutter,
        click: () => {
          useFileActions().declutter({
            context: tag,
            contextType: FILE_CONTEXTS.tag
          })
        }
      },
      viewSlideshow: {
        label: 'View Slideshow',
        icon: COMMON_ICONS.slideshow,
        click: () => useSlideshow({
          container: tag,
          fileContext: FILE_CONTEXTS.tag,
          userId,
          shareToken
        }).start()
      },
      print: {
        label: 'Print',
        icon: COMMON_ICONS.print,
        click: () => {
          if (!tag.files_count) {
            useInfoToast().add({
              description: `You must add files to this tag before you can Print it.`
            });
          } else {
            useProjectActions().openContainerPrintModal({
              context: tag,
              contextType: FILE_CONTEXTS.tag
            });
          }
        }
      },
      digitize: {
        label: 'Digitize Media',
        icon: COMMON_ICONS.digitize,
        click: () => useCommonModals().openDigitizationUpsellModal({
          context: DIGITIZATION_UPSELL_CONTEXTS.tagContextMenu
        })
      },
      download: {
        label: 'Download Files',
        icon: COMMON_ICONS.download,
        click: () => {
          if (!tag.files_count) {
            useInfoToast().add({
              description: `You must add files to this tag before you can download it.`
            });
          } else {
            useFileActions().downloadContext({
              context: tag,
              contextType: FILE_CONTEXTS.tag,
              userId
            });
          }
        }
      }
    }
  }

  return {
    addEditTag,
    share,
    editThumbnail,
    confirmTagMerge,
    deleteTags,
    getContextActions
  };
}
